import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; // Added for page navigation
import Papa from "papaparse";
import apiInstance from "../../../utility/apiInstance";

interface ErrorLog {
  row: number;
  programName: string;
  message: string;
}

const BulkInstutionProgramsUpload: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [status, setStatus] = useState({
    success: 0,
    failed: 0,
    total: 0,
    currentMsg: "",
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setErrors([]);
    
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        const cleanedData = results.data.map((row: any) => {
          const cleanRow: any = {};
          Object.keys(row).forEach((key) => {
            cleanRow[key.trim()] = row[key];
          });
          return cleanRow;
        });
        setPreview(cleanedData.slice(0, 5));
        setStatus(prev => ({ ...prev, total: cleanedData.length, success: 0, failed: 0 }));
      },
    });
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setErrors([]);
    let successCount = 0;
    let failCount = 0;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const programs = results.data;

        for (let i = 0; i < programs.length; i++) {
          const row = programs[i] as any;
          const pName = row.programName || row["Program Name"] || `Row ${i + 1}`;
          
          try {
            const programData = {
              programName: pName,
              about: row.about || row.About || "",
              requirement: row.requirement || row.Requirement || "",
              institution: row.institutionId || row["Institution ID"], 
              applicationFee: parseFloat(row.applicationFee || 0),
              tuitionFee: row.tuitionFee || "",
              programLevel: row.programLevel || "undergraduate",
              field: row.field || "arts",
              start: row.start || new Date(),
              end: row.end || new Date(),
            };

            await apiInstance.post("/api/programs", programData);
            successCount++;
          } catch (err: any) {
            failCount++;
            const errMsg = err?.response?.data?.errors?.[0]?.message || 
                           err?.response?.data?.message || 
                           "Unknown server error";
            
            setErrors(prev => [...prev, {
              row: i + 1,
              programName: pName,
              message: errMsg
            }]);
          }

          setStatus({
            total: programs.length,
            success: successCount,
            failed: failCount,
            currentMsg: `Processing ${i + 1} of ${programs.length}...`,
          });
        }
        setUploading(false);
      }
    });
  };

  const downloadTemplate = () => {
    const template = `institutionId,programName,about,requirement,applicationFee,tuitionFee,programLevel,field,start,end
INST_123,Bachelor of Science,About text,Requirements,100,15000,undergraduate,science,2025-09-01,2029-06-30`;
    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_upload_template.csv";
    a.click();
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Global Bulk Program Upload</h2>
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-2"></i> Back
        </button>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body">
          {/* Step 1: File Setup */}
          <div className="row g-4 mb-4">
            <div className="col-md-7">
              <label className="form-label fw-bold">1. Upload CSV File</label>
              <input 
                type="file" 
                accept=".csv" 
                className="form-control form-control-lg" 
                onChange={handleFileChange} 
                disabled={uploading} 
              />
              <p className="form-text mt-2">
                Your CSV must include an <code>institutionId</code> column for each program.
              </p>
            </div>
            <div className="col-md-5 d-flex align-items-end">
              <button className="btn btn-light border w-100" onClick={downloadTemplate}>
                <i className="fas fa-download me-2"></i> Download Template
              </button>
            </div>
          </div>

          {/* Step 2: Progress & Stats */}
          {(uploading || status.total > 0) && (
            <div className="bg-light p-4 rounded mb-4">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">{uploading ? "Uploading..." : "Upload Summary"}</span>
                <span>{status.success + status.failed} / {status.total}</span>
              </div>
              <div className="progress mb-3" style={{ height: "12px" }}>
                <div 
                  className={`progress-bar ${uploading ? 'progress-bar-striped progress-bar-animated' : ''}`}
                  style={{ width: `${( (status.success + status.failed) / status.total) * 100}%` }}
                ></div>
              </div>
              <div className="row text-center">
                <div className="col"><small className="text-muted d-block">Success</small><span className="text-success fw-bold">{status.success}</span></div>
                <div className="col"><small className="text-muted d-block">Failed</small><span className="text-danger fw-bold">{status.failed}</span></div>
              </div>
            </div>
          )}

          {/* Step 3: Action Button */}
          <div className="mb-4">
            <button 
              className="btn btn-primary btn-lg w-100" 
              onClick={handleUpload} 
              disabled={!file || uploading}
            >
              {uploading ? "Please wait while we process the rows..." : "Start Bulk Upload"}
            </button>
          </div>

          {/* Error Log Section (Last) */}
          {errors.length > 0 && (
            <div className="mt-5">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="text-danger mb-0"><i className="fas fa-exclamation-circle me-2"></i>Error Log</h5>
                <span className="badge bg-danger">{errors.length} Issues Found</span>
              </div>
              <div className="table-responsive border rounded shadow-sm" style={{ maxHeight: "400px" }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: "80px" }}>Row</th>
                      <th style={{ width: "250px" }}>Program Name</th>
                      <th>Error Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((err, idx) => (
                      <tr key={idx}>
                        <td className="text-muted fw-bold">{err.row}</td>
                        <td>{err.programName}</td>
                        <td className="text-danger small">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkInstutionProgramsUpload;